<template>
  <ion-header>
    <ion-toolbar>
      <ion-title v-if="isEditingPost">{{ t('PostPage.editPost') }}</ion-title>
      <ion-title v-else>{{ t('PostPage.newPost') }}</ion-title>
      <ion-buttons slot="end">
        <ion-button slot="icon-only" @click="closeModal()"><ion-icon name="close"></ion-icon></ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content :fullscreen="true">
    
    <ion-item>
      <ion-label position="stacked">{{ t('PostPage.postTitle') }}</ion-label>
      <ion-input v-model="post.title"></ion-input>
    </ion-item>
    <ion-item>
      <ion-label position="stacked">{{ t('PostPage.postContent') }}</ion-label>
      <ion-textarea v-model="post.content"></ion-textarea>
    </ion-item>
    
    <ion-item>
      <ion-label position="stacked" color="primary">{{ t('PostPage.postImage') }}</ion-label>
      <ion-input type="file" accept="image/*" @change="onPostImageFileChange($event)"></ion-input>
      <ion-buttons slot="end" v-if="post.image">
        <ion-button slot="icon-only" @click="clearUploadedPostImage()"><ion-icon :icon="close"></ion-icon></ion-button>
      </ion-buttons>
    </ion-item>
    <img v-if="post.image" :src="post.image" style="display: block; margin: auto" />
  </ion-content>

  <ion-footer>
    <ion-toolbar>
      <ion-button v-if="isEditingPost" expand="block" @click="updatePost()">
        {{ t('PostPage.updatePost') }}
      </ion-button>
      <ion-button v-else expand="block" @click="createNewPost(post.title, post.content)">
        {{ t('PostPage.publishPost') }}
      </ion-button>
    </ion-toolbar>
  </ion-footer>

</template>

<script lang="ts">
// Vue reactivity
import { ref, reactive, defineComponent, watchEffect } from 'vue';

// icons
import { thumbsUpOutline, thumbsDownOutline, add, close } from 'ionicons/icons';

// components
import { IonHeader, IonToolbar, IonTitle, IonContent, IonFooter, IonSpinner, IonItem, IonLabel, IonIcon,
        IonThumbnail, IonAvatar, IonButtons, IonButton, IonInput, IonTextarea, modalController, loadingController, } from '@ionic/vue';

// API services
import PostService from '@/services/PostService';

import { utils } from '@/composables/utils';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'PostModal',
  props: {
    editingPost: null,
  },
  components: { IonHeader, IonToolbar, IonTitle, IonContent, IonFooter,
                IonSpinner, IonItem, IonLabel, IonIcon,
                IonThumbnail, IonAvatar, IonButtons, IonButton, IonInput, IonTextarea },
  setup(props) {
    // 1. declare state variables (ref to make them reactive)
    const post = reactive({
      id: null,
      title: "",
      content: "",
      image: "",
    });
    const postImageFile = ref<any>(null);
    const isEditingPost = ref(false);

    if (props.editingPost) { // editing post
      isEditingPost.value = true;
      watchEffect(() => {
        const { id, title, content, image } = props.editingPost.value;
        post.id = id;
        post.title = title;
        post.content = content;
        post.image = image;
      })
    }

    // 2. fetch data from API and store data in state variables
    // TODO (for editing post)

    // methods or filters
    const { t } = useI18n();
    const { formatDate, presentToast } = utils();

    const closeModal = async (data: any = {}) => {
      await modalController.dismiss(data);
    };
    const onPostImageFileChange = (e: any) => {
      if (e.target.files && e.target.files[0]) {
        postImageFile.value = e.target.files[0];
        const reader = new FileReader();
        reader.onload = (e: any) => {
          post.image = e.target.result;
        }
        reader.readAsDataURL(postImageFile.value); // convert to base64 string and preview it
        e.srcElement.value = null;
      }
    }
    const clearUploadedPostImage = () => {
      post.image = "";
      postImageFile.value = null;
    }

    const createNewPost = async (title: string, content: string) => {
      const loading = await loadingController.create({});
      await loading.present();
      const res = await PostService.addNewPost(title, content, postImageFile.value);
      loading.dismiss();
      presentToast( t('PostPage.successPublishPost'), 2000 );
      closeModal({ newPostCreated: true });
    };

    const updatePost = async() => {
      const loading = await loadingController.create({});
      await loading.present();
      const originalImageLink = props.editingPost.value.image;
      const res = await PostService.updatePost(post.id, post.title, post.content, post.image, postImageFile.value, originalImageLink);
      loading.dismiss();
      presentToast( t('PostPage.successUpdatePost'), 2000 );
      closeModal();
    }

    // 3. return variables & methods to be used in template HTML
    return {
      t,

      // icons
      thumbsUpOutline, thumbsDownOutline, add, close,

      // variables
      post, postImageFile, isEditingPost,

      // methods
      formatDate, createNewPost, updatePost, closeModal,
      onPostImageFileChange, clearUploadedPostImage
    }
  }
});
</script>